import { PaneHeader, PaneWrapper } from '@campfire/pane';
import { Box, Grid, Input, MenuItem, Select, Tab, Tabs, Theme } from '@material-ui/core';
import { Add } from '@material-ui/icons';
import { createStyles, makeStyles } from '@material-ui/styles';
import { TabletButton } from '@campfire/tablet-button';
import React, { useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router';
import { useCampfireTheme } from '../../../theme/useCampfireTheme';
import { useAdvancedCommunications } from './use-advanced-communications';
import { useCanEditBulletins } from '../bulletin-management/use-can-edit-bulletins';
import { CommunicateButton } from '../volunteer-common-profile/profile-sections/action-menu/CommunicateButton';

const useTabClasses = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      boxSizing: 'border-box',
      minWidth: 72,
      textTransform: 'none',
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      '&:hover': {
        color: theme.palette.primary.light,
        opacity: 1,
      },
      '&$selected': {
        color: theme.palette.primary.main,
      },
      '&:focus': {
        color: theme.palette.primary.main,
      },
    },
    selected: {},
  })
);

export const BULLETINS_PATH = '/management/communications/bulletins';

export const COMMUNICATIONS_TAB_ITEMS: Array<{ value: string; label: string; advancedOnly?: boolean }> = [
  { label: 'Inbox', value: '/management/communications/inbox', advancedOnly: true },
  { label: 'Bulletins', value: '/management/communications/bulletins' },
  { label: 'Sent', value: '/management/communications/sent' },
];

export const useCommunicationsTabItems = () => {
  const advanced = useAdvancedCommunications();
  return COMMUNICATIONS_TAB_ITEMS.filter((tab) => advanced || !tab.advancedOnly);
};

interface CommunicationsTabsProps {
  onSent?: () => void;
}

const CommunicationsTabs = ({ onSent }: CommunicationsTabsProps) => {
  const routeMatch = useRouteMatch();
  const history = useHistory();
  const classes = useTabClasses();
  const tabItems = useCommunicationsTabItems();
  const canEditBulletins = useCanEditBulletins();
  const selectedTabIndex = tabItems.findIndex((x) => x.value === routeMatch?.path);
  const selectedTab = tabItems[selectedTabIndex] ?? tabItems[0];
  const onBulletins = routeMatch?.path === BULLETINS_PATH;

  return selectedTab ? (
    <Box
      borderBottom='solid #dadada 1px'
      display='flex'
      justifyContent='space-between'
      alignContent='center'
      alignItems='center'
      width={1}
    >
      <Tabs
        indicatorColor='primary'
        value={selectedTabIndex === -1 ? 0 : selectedTabIndex}
        aria-label='communications tabs'
      >
        {tabItems.map((x) => (
          <Tab
            disableRipple
            disableTouchRipple
            classes={classes}
            key={x.value}
            label={x.label}
            onClick={() => {
              history.push(x.value);
            }}
          />
        ))}
      </Tabs>
      <Box display='flex' justifyContent='flex-end' alignContent='center' alignItems='center'>
        <CommunicateButton
          allowRecipientSelection
          dataTrack='fs-comms-contact-button'
          onSent={onSent}
          onNewBulletin={canEditBulletins ? () => history.push(`${BULLETINS_PATH}?new=1`) : undefined}
        />
        {onBulletins && canEditBulletins && (
          <TabletButton
            variant='contained'
            color='primary'
            onClick={() => history.push(`${BULLETINS_PATH}?new=1`)}
            startIcon={<Add style={{ fontSize: 16 }} />}
            style={{
              borderRadius: 4,
              textTransform: 'none',
              padding: '6px 12px',
              whiteSpace: 'nowrap',
              marginRight: 10,
            }}
          >
            Add bulletin
          </TabletButton>
        )}
      </Box>
    </Box>
  ) : null;
};

const useInputStyles = makeStyles(() =>
  createStyles({
    input: {
      fontSize: '16px',
      fontWeight: 600,
      padding: '8px 0',
    },
  })
);

const CommunicationsTabsMobile = () => {
  const history = useHistory();
  const routeMatch = useRouteMatch();
  const inputStyles = useInputStyles();
  const tabItems = useCommunicationsTabItems();
  const [value, setValue] = useState<string>(routeMatch?.path ?? tabItems[0].value);

  return (
    <Grid container justify='space-between' alignItems='center'>
      <Grid item>
        <Select
          MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
          value={value}
          onChange={(event) => {
            const selectedValue = event.target.value as string;
            history.push(selectedValue);
            setValue(selectedValue);
          }}
          input={<Input classes={inputStyles} />}
          renderValue={(selected) => {
            const selectedValue = selected as string;
            return tabItems.find((x) => x.value === selectedValue)?.label;
          }}
        >
          {tabItems.map((tabItem) => (
            <MenuItem key={tabItem.value} value={tabItem.value}>
              {tabItem.label}
            </MenuItem>
          ))}
        </Select>
      </Grid>
    </Grid>
  );
};

interface CommunicationsTabsWrapperProps {
  children: React.ReactNode;
  onSent?: () => void;
}

export const CommunicationsTabsWrapper = ({ children, onSent }: CommunicationsTabsWrapperProps) => {
  const { isMobile } = useCampfireTheme();
  return (
    <PaneWrapper>
      <PaneHeader>{isMobile ? <CommunicationsTabsMobile /> : <CommunicationsTabs onSent={onSent} />}</PaneHeader>
      {children}
    </PaneWrapper>
  );
};
